import { ImageComponent } from 'components/Image';
import { ProgressBarPalette } from 'components/progressBars/helpers';
import { SmallProgressBar } from 'components/progressBars/SmallProgressBar';
import { useTheme } from 'hooks/theme';
import {
  HEADLINE_PROMO_NS,
  PROGRESS_STEPS,
} from 'modules/headlinePromo/constants';
import { useLevels } from 'modules/headlinePromo/hooks/useLevels';
import type { FC } from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'react-navi';
import { ScHeadlinePromoPanel } from './styled';

const ICON_SIZE = 24;
const ICON_SIZE_MOBILE = 56;

export interface IHeadlinePromoPanelProps {
  onLinkClick?: () => void;
  isMobile?: boolean;
}

// the artifact changes with the level, same asset as the reward card
export const HeadlinePromoPanel: FC<IHeadlinePromoPanelProps> = memo(
  ({ onLinkClick, isMobile }) => {
    const { t } = useTranslation(HEADLINE_PROMO_NS);
    const { navigate } = useNavigation();
    const { images } = useTheme();
    const { currentLevel, progress, hasStarted, issuedLevel, isPassCompleted } =
      useLevels();

    const iconSize = isMobile ? ICON_SIZE_MOBILE : ICON_SIZE;

    // the pass is over only once nothing is left waiting
    const isPassOver = isPassCompleted && !issuedLevel;

    const statusText = useMemo(() => {
      if (!hasStarted) return t('panel.rewardsWaiting');
      if (isPassOver) return t('panel.bonusClaimed');

      return null;
    }, [hasStarted, isPassOver, t]);

    return (
      <ScHeadlinePromoPanel
        onClick={() => {
          onLinkClick?.();
          navigate('/headline-promo');
        }}
      >
        <div className="inner">
          <div className="row">
            <ImageComponent
              alt={t(`levels.${currentLevel.key}.name`)}
              className="artifact"
              height={iconSize}
              src={currentLevel.artifact}
              width={iconSize}
            />
            <p className="title">{t('title')}</p>
          </div>
          <div className="content">
            {statusText ? (
              <p className="teaser">{statusText}</p>
            ) : (
              <>
                {!isMobile && (
                  <SmallProgressBar
                    config={{
                      dotsCount: PROGRESS_STEPS.length,
                      color: ProgressBarPalette.PURPLE,
                    }}
                    userProgress={progress}
                  />
                )}
                <div className="value-wrapper">
                  <p className="progress-value">
                    <span className="label">{t('panel.yourProgress')}: </span>
                    {`${progress}%`}
                  </p>
                  {isMobile && (
                    <p className="reward-value">
                      <span className="label">{t('panel.yourReward')}: </span>
                      {t(`levels.${currentLevel.key}.reward`)}
                    </p>
                  )}
                </div>
                {isMobile && (
                  <SmallProgressBar
                    config={{
                      dotsCount: PROGRESS_STEPS.length,
                      color: ProgressBarPalette.PURPLE,
                    }}
                    userProgress={progress}
                  />
                )}
              </>
            )}
          </div>
          {isMobile && <images.arrowRightSmallIcon.component />}
        </div>
      </ScHeadlinePromoPanel>
    );
  }
);
